/** @format */

import {
	Box,
	Container,
	Link,
	Heading,
	Text,
	Grid,
	GridItem,
} from '@chakra-ui/layout';
import {useColorMode} from '@chakra-ui/react';
import {NextSeo} from 'next-seo';
import {ReactElement, useEffect, useState} from 'react';
import MenuButton from '../components/atoms/buttons/menu-button/menu-button';
import Layout from '../components/Layout';
import {BulbLogo} from '../components/molecules/main-menu-nav/bulb-logo';
import Footer from '../components/organisms/footer/footer';
import Header from '../components/organisms/header/header';
import MainMenu from '../components/organisms/main-menu/main-menu';
import {getMainMenu} from '../lib/api/option-pages';
import Patterns from '../patterns/patterns';
import {footerData} from '../utils';
import styles from '../components/organisms/wysiwyg/wysiwyg.module.scss';

const topLevelPagesKey = 'Top Level Pages';
const fetchXMLFile = async filePath => {
	try {
		const response = await fetch(filePath);
		if (!response.ok) {
			throw new Error(`HTTP error! status: ${response.status}`);
		}
		return await response.text();
	} catch (err) {
		console.error('Error fetching XML file:', err);
		throw err;
	}
};

const parseXML = xmlData => {
	const parser = new DOMParser();
	const xmlDoc = parser.parseFromString(xmlData, 'text/xml');
	return xmlDoc.getElementsByTagName('url');
};

const addChildToOrphanPages = groupedPages => {
	// Add orphaned pages to the Top Level Pages group
	Object.entries(groupedPages).map(([basePath, pages]) => {
		if (basePath && basePath !== topLevelPagesKey) {
			const parentFinder = groupedPages[topLevelPagesKey].find(item =>
				item.url.includes(basePath.toLowerCase()),
			);

			if (parentFinder) {
				parentFinder.child = pages;
			}
		}
	});
};

const processURLs = (
	urls: HTMLCollectionOf<Element>,
): {[key: string]: {label: string; url: string}[]} => {
	const excludeList = ['/patterns', '/pattern-list'].map(path =>
		path.toLowerCase(),
	);

	const groupedPages = {};

	Array.from(urls).forEach(urlElement => {
		try {
			const url = (urlElement as Element).childNodes[0].textContent.trim();
			const urlObj = new URL(url);
			if (excludeList.includes(urlObj.pathname.toLowerCase())) {
				return;
			}
			const segments = urlObj.pathname.split('/').filter(segment => segment);

			let basePath = topLevelPagesKey; // Default group
			let pageName = segments.join(' '); // Default to full path if no segments
			const segmentLength = segments.length;

			switch (segmentLength) {
				case 0:
					// homepage
					if (urlObj.pathname === '/') {
						pageName = 'Home';
					}
					break;
				case 1:
					// orphaned pages
					pageName = segments[0]
						.replaceAll('-', ' ')
						.replaceAll('%2F', ' ')
						.replace(/\b\w/g, l => l.toUpperCase());
					break;
				default:
					// child pages
					basePath = segments[0]; // Use the first segment as the base path
					pageName = segments[segments.length - 1]
						.replaceAll('-', ' ')
						.replaceAll('%2F', ' ')
						.replace(/\b\w/g, l => l.toUpperCase());
					break;
			}

			if (!groupedPages[basePath]) {
				groupedPages[basePath] = [];
			}
			if (pageName) {
				groupedPages[basePath].push({label: pageName, url});
			}
		} catch (error) {
			console.error('Error processing URL:', error);
		}
	});

	addChildToOrphanPages(groupedPages);

	return groupedPages;
};

function SiteMap({seo, mainMenu, footerData, fields}): ReactElement {
	const [groupedPages, setSiteMapContentGroup] = useState([]);
	const [error, setError] = useState(false);
	const {colorMode} = useColorMode();
	const [showMainMenu, setShowMainMenu] = useState(false);

	useEffect(() => {
		const handleXMLData = async () => {
			try {
				const xmlData = await fetchXMLFile('/sitemap-0.xml');
				const urls = parseXML(xmlData);
				const grouped = processURLs(urls);
				// @ts-ignore
				setSiteMapContentGroup(grouped);
			} catch (error) {
				setError(true);
				console.error('Failed to process XML data:', error);
			}
		};

		handleXMLData();
	}, []);

	function onClickMenu() {
		setShowMainMenu(!showMainMenu);
	}

	if (error) {
		return <p>Error loading site map.</p>;
	}

	return (
		<Layout
			seo={{
				title: seo.title,
				description: seo.description,
			}}>
			<NextSeo {...seo} />

			{showMainMenu && <MainMenu onClick={onClickMenu} />}
			{fields?.map((post: any, index: number) => {
				return (
					<Patterns
						fieldGroupName={post.__typename}
						post={post}
						colorMode={colorMode}
						key={`${post.__typename}_${index + 1}`}
					/>
				);
			})}

			<Box className={`wrapper WYSIWYG--default`}>
				<Box
					display={'flex'}
					alignItems={'center'}
					justifyContent={'space-between'}>
					<Link href={'/'}>
						<a>
							<Box cursor={'pointer'}>
								{colorMode === 'dark' ? (
									<BulbLogo fill='white' />
								) : (
									<BulbLogo />
								)}
							</Box>
						</a>
					</Link>
					<MenuButton labelText='Menu' onClick={onClickMenu} />
				</Box>
			</Box>

			<Container maxW='container.xl' mx='auto'>
				<Box pt={{base: '10', md: '12'}} pb={{base: '16', md: '20'}}>
					<Header
						// @ts-ignore
						data={{
							minH: 'unset',
							pageTitle: '',
							headerHeroText: {
								text: 'Sitemap',
								fontSize: '64px',
							},
						}}
					/>

					<Grid
						className={`${styles.wysiwigWrapper} WYSIWYG--default`}
						templateColumns={{
							base: 'repeat(1, 1fr)',
							md: 'repeat(2, 1fr)',
						}}
						gap={6}>
						{Object.entries(groupedPages).map(([basePath, pages]) => (
							<GridItem
								key={basePath}
								className={styles.textStyles + 'GridItem'}
								textStyle={{
									base: 'display6xl',
									color: 'midnightBlue',
								}}
								mb='8'>
								{basePath ? (
									<h4
										style={{
											textTransform: 'capitalize',
										}}>
										{basePath} {`(${pages.length})`}
									</h4>
								) : null}

								<Box as='ul'>
									{pages.map((page, i) => (
										<li
											key={`sitemap-${i}-${page.url}-${page.label}`}
											style={{
												marginBottom: 8,
											}}
											title={page.url}>
											<Link href={page.url}>
												{page.label}
												{page.child ? (
													<Box as='span'> ({page.child.length})</Box>
												) : null}
											</Link>
										</li>
									))}
								</Box>
							</GridItem>
						))}
					</Grid>
				</Box>
			</Container>
			<Footer footerData={footerData} />
		</Layout>
	);
}
// test comment
export async function getStaticProps({params, preview, previewData}) {
	const mainMenu = await getMainMenu();
	let fields = [];
	fields.unshift(mainMenu);

	return {
		props: {
			fields,
			mainMenu,
			footerData,
			seo: {
				title: 'Sitemap',
				description:
					'See all the pages of our website and quickly find what you’re looking for!',
			},
		},
	};
}
export default SiteMap;
